import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, InputAdornment, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
  createAdminConfig, updateAdminConfig,
  selectAdminSaving,
} from '../../reducers/Beacon4PerformanceNetworkOverviewAdminReducer'

const TECHS       = ['2G', '3G', '4G', '5G']
const CATEGORIES  = ['Main KPI', 'Breakdown', 'Breakdown2']
const UNITS       = ['%', '#', 'erl', 'MB']

// Data-source defaults for a brand-new KPI.
//
// These are seeds for the Formula tab's auto-fill, not values the user is shown
// here — this dialog no longer displays them. viewName and sqlExpression stay
// empty on purpose: a KPI is created as a shell and its formula is authored on
// the Formula tab, which has the room and the column browser to do it properly.
//
// isActive is false for a blank create, and only for a blank create: a KPI with
// no data source must not go live. Edit and clone both spread over this, so an
// existing KPI keeps its own flag and a clone inherits the source KPI's — a
// clone arrives complete, so it can be active immediately.
const EMPTY = {
  tech: '2G', category: 'Main KPI', kpiKey: '', kpiName: '',
  unit: '%', isRate: false, slaGreen: null, slaRed: null,
  displayOrder: 0, isActive: false,
  viewName: '', formulaType: 'SIMPLE', sqlExpression: '',
  dateCol: 'Date', sideCol: 'Side', polygonCol: 'Polygon4', jvidCol: 'JV_ID',
  mycomCol: 'Mycom_id', vendorCol: 'VENDOR', importDateCol: '',
  note: '',
}

// Compact eyebrow header for a form section.
function SectionLabel({ children }) {
  return (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 700, color: 'text.secondary',
      textTransform: 'uppercase', letterSpacing: '0.08em',
      mb: 1.25, mt: 0.5,
    }}>
      {children}
    </Typography>
  )
}

/**
 * Create, edit, or clone a KPI's business metadata.
 *
 * Three modes, resolved from the props:
 *   - `config` set        → Edit an existing KPI (kpiKey locked).
 *   - `cloneSource` set    → Create a NEW KPI pre-filled from an existing one
 *                            (same as Create under the hood — it still goes
 *                            through createAdminConfig — just with the form
 *                            seeded from cloneSource instead of blank).
 *   - neither set          → Plain "New KPI", blank form.
 *
 * Deliberately does NOT touch the data source or the formula.
 *
 * It used to carry a "Data source & formula" accordion that duplicated the
 * Formula tab in a fraction of the space: a cramped column mapping, a function
 * palette and a SQL box with no room for the column browser that makes any of
 * it usable. Two implementations of the same screen also meant two places to
 * fix every bug. The dialog now creates the shell and hands off — the parent
 * selects the new KPI and opens it on Formula.
 */
export default function Beacon4PerformanceNetworkOverviewAdminMetadataDialog({ open, config, cloneSource, onClose, onSaved }) {
  const dispatch = useDispatch()
  const saving   = useSelector(selectAdminSaving)

  const isNew   = !config
  const isClone = isNew && !!cloneSource
  const [form, setForm] = useState(EMPTY)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    if (!open) return
    if (config) {
      setForm({ ...EMPTY, ...config, note: '' })
    } else if (cloneSource) {
      // Carry over everything except identity — a clone is still a brand
      // new record, so kpiKey (permanent once saved) and the display name
      // must be deliberately chosen, not silently duplicated.
      setForm({
        ...EMPTY, ...cloneSource,
        kpiKey:  `${cloneSource.kpiKey}_copy`,
        kpiName: `${cloneSource.kpiName} (Copy)`,
        note: '',
      })
    } else {
      setForm(EMPTY)
    }
    setSaveError(null)
  }, [open, config, cloneSource])

  const set = (k) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value
    setForm(f => ({ ...f, [k]: v }))
  }
  const setNum = (k) => (e) => {
    const raw = e.target.value
    setForm(f => ({ ...f, [k]: raw === '' ? null : Number(raw) }))
  }

  // No view/SQL gate any more — those are the Formula tab's job. Trim-safe
  // because {...EMPTY, ...config} lets the API's nulls through.
  const canSave =
    !!form.tech &&
    !!(form.kpiKey ?? '').trim() &&
    !!(form.kpiName ?? '').trim() &&
    !saving

  // Stays open and says so when the save fails. It used to close regardless,
  // which threw away everything the user had typed with no message at all —
  // the failure was indistinguishable from success.
  const onSave = async () => {
    const { id, ...dto } = form
    setSaveError(null)

    const res = isNew
      ? await dispatch(createAdminConfig(dto))
      : await dispatch(updateAdminConfig({ id: config.id, dto }))

    if (res?.error) {
      setSaveError(
        isNew
          ? 'Could not create the KPI. The KPI key may already be in use.'
          : 'Could not save the changes.'
      )
      return
    }

    // On create, hand the new id up so the caller can select it and open the
    // Formula tab — that is where the data source and formula are set now.
    onSaved?.(isNew ? { id: res?.payload?.id ?? null, dto } : { dto })
    onClose()
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 1.5 } }}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
        {isClone ? 'Clone KPI' : (isNew ? 'New KPI' : 'Edit KPI')}
        {!isNew && (
          <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: 'text.secondary' }}>
            {config?.kpiName} · {config?.kpiKey}
          </Typography>
        )}
        {isClone && (
          <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: 'text.secondary' }}>
            from {cloneSource?.kpiName} · {cloneSource?.kpiKey}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2.5, fontSize: 11.5 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}
        {isClone && (
          <Alert severity="info" sx={{ mb: 2.5, fontSize: 11.5 }}>
            Formula, presentation and column mapping were copied from <strong>{cloneSource?.kpiKey}</strong>.
            Pick a unique KPI key and name below, then adjust anything else the clone needs.
          </Alert>
        )}

        {/* ───────────── Identity ───────────── */}
        <SectionLabel>Identity</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Tech" value={form.tech} onChange={set('tech')}>
              {TECHS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Category" value={form.category} onChange={set('category')}>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="KPI key" value={form.kpiKey} onChange={set('kpiKey')}
              placeholder="vssr" disabled={!isNew}
              helperText={isNew ? 'Stable identifier — lowercase, no spaces. Cannot be changed later.' : 'Identifier is permanent.'}
              FormHelperTextProps={{ sx: { fontSize: 10 } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Display name" value={form.kpiName} onChange={set('kpiName')}
              placeholder="e.g. Voice Setup Success Rate" />
          </Grid>
        </Grid>

        {/* ───────────── Presentation ───────────── */}
        <Box sx={{ mt: 3 }}>
          <SectionLabel>Presentation</SectionLabel>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Unit" value={form.unit} onChange={set('unit')}>
                {UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="SLA green"
                value={form.slaGreen ?? ''} onChange={setNum('slaGreen')}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: 11 } }}>{form.unit}</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="SLA red"
                value={form.slaRed ?? ''} onChange={setNum('slaRed')}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: 11 } }}>{form.unit}</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Display order"
                value={form.displayOrder} onChange={setNum('displayOrder')} />
            </Grid>
            {/* Chart style used to live here, per-KPI. It's now a property of
                the breakdown mapping instead (Manage KPIs > Breakdowns), so
                the same KPI can be a plain line in one chart and a shaded
                area in another, depending on where it's used. */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Switch checked={form.isRate} onChange={set('isRate')} size="small" />}
                  label={<Typography sx={{ fontSize: 12 }}>Lower is better</Typography>}
                />
                <FormControlLabel
                  control={<Switch checked={form.isActive} onChange={set('isActive')} size="small" />}
                  label={<Typography sx={{ fontSize: 12 }}>Active</Typography>}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>


        {/* ───────────── Change note ───────────── */}
        <Box sx={{ mt: 3 }}>
          <SectionLabel>Audit</SectionLabel>
          <TextField fullWidth size="small" label="Change note"
            value={form.note ?? ''} onChange={set('note')}
            placeholder="Optional — short description of what you changed and why" />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={!canSave} sx={{ minWidth: 130 }}>
          {saving ? 'Saving…' : (isClone ? 'Create clone' : (isNew ? 'Create KPI' : 'Save changes'))}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
